/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  ReactElement,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';

import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CSSProperties } from 'styled-components';

import { ArrowRightRed, Delete, Edit, Order } from '~/assets/icons';
import { Table, Button, Pagination, Loading } from '~/components';
import { PageOptionsRequest } from '~/components/Pagination';
import {
  IPagination,
  OperatorsSortBy,
  SuggestOperatorsSortBy,
} from '~/models/Common';
import { IOperator, ISuggestedOperator } from '~/models/Operator';
import { AdminService } from '~/services';
import { handleFormatDate } from '~/utils/functions';

import * as S from './styles';
import ConfirmDeleteLivesModal from './DeleteLifes';
import LifesFormModal from './LifesForm';
import LifesService from '~/services/lifes';
import { Plan } from '~/models/lifes';

type OperatorStatus = 'saved' | 'suggested';

function Lifes(): ReactElement {
  const [lifes, setLifes] = useState<Plan[]>([]);
  const [suggestedOperators, setSuggestedOperators] = useState<
    IPagination<ISuggestedOperator[]>
  >({ data: [], lastPage: 0, page: 1, perPage: 1, total: '0' });
  const [sortByOperators, setSortByOperators] =
    useState<OperatorsSortBy>('name');
  const [sortBySuggestOperators, setSortBySuggestOperators] =
    useState<SuggestOperatorsSortBy>('name');
  const [operatorsStatusFilter, setOperatorsStatusFilter] =
    useState<OperatorStatus>('saved');
  const [saveOperatorModal, setSaveOperatorModal] = useState<boolean>(false);
  const [pageOptions, setPageOptions] = useState<PageOptionsRequest>({
    page: 1,
  });
  const router = useHistory();
  const [deleteOperatorModal, setDeleteOperatorModal] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [editingLife, setEditingLife] = useState<Plan | undefined>(undefined);
  const [lifeToDelete, setLifeToDelete] = useState<Plan | undefined>(undefined);

  const [direction, setDirection] = useState<string>('asc');

  const handleDirection = (): void => {
    setDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
  };

  const goToDetailPageButtonStyle = useRef<CSSProperties>({
    width: 9,
    height: 19,
  });

  const newOperatorButtonStyle = useRef<CSSProperties>({
    borderRadius: '10px',
    maxWidth: '214px',
  });

  const handleSwapOperatorStatus = useCallback((status: OperatorStatus) => {
    setOperatorsStatusFilter(status);
  }, []);

  // Abrir modal para criar nova vida
  const handleOpenSaveOperatorModal = useCallback(() => {
    setEditingLife(undefined);
    setIsEdit(false);
    setSaveOperatorModal(true);
  }, []);

  // Abrir modal para editar vida existente
  const handleEditLife = useCallback((life: Plan) => {
    setEditingLife(life);
    setIsEdit(true);
    setSaveOperatorModal(true);
  }, []);

  // Abrir modal de exclusão
  const handleDeleteLife = useCallback((life: Plan) => {
    setLifeToDelete(life);
    setDeleteOperatorModal(true);
  }, []);

  const handleGetLifes = useCallback(async () => {
    try {
      const response = await LifesService.getAllLifes();
      setLifes(response.data);
    } catch (error) {
      toast.error(`Não foi possível listar as vidas.`);
    }
  }, [pageOptions, sortByOperators, direction]);

  useEffect(() => {
    handleGetLifes();
  }, [
    pageOptions,
    saveOperatorModal,
    operatorsStatusFilter,
    handleGetLifes,
    sortBySuggestOperators,
    sortByOperators,
    direction,
  ]);

  const handleSubmitLife = async (data: {
    id?: string;
    quantity: string;
    credits: number;
    isEdit: boolean;
  }) => {
    try {
      if (isEdit && data.id) {
        await LifesService.updateLife(data.id, {
          name: String(data.quantity),
          credit_price: data.credits,
        });
        toast.success('Vida editada com sucesso!');
      } else {
        await LifesService.createLife({
          name: String(data.quantity),
          credit_price: data.credits,
        });
        toast.success('Vida criada com sucesso!');
      }
      setSaveOperatorModal(false);
      setEditingLife(undefined);
      setIsEdit(false);
      handleGetLifes();
    } catch (error) {
      toast.error('Erro ao salvar a vida');
    }
  };

  return (
    <>
      <S.Container>
        <S.Title>GERENCIAMENTO DE VIDAS</S.Title>
        <S.TableWrapper>
          <Table
            key={0}
            noItems={lifes.length === 0}
            tableHeadChildren={() => (
              <>
                <th
                  onClick={() => {
                    setSortByOperators('name');
                    handleDirection();
                  }}
                >
                  Tipos de vida do lead
                </th>
                <th
                  onClick={() => {
                    setSortByOperators('sellers_qnt');
                    handleDirection();
                  }}
                >
                  Quantidade de créditos
                </th>
                <th
                  onClick={() => {
                    setSortByOperators('status');
                    handleDirection();
                  }}
                >
                  Ações
                </th>
                <th> </th>
              </>
            )}
          >
            {lifes?.map((life) => (
              <tr key={life.id}>
                <td>{life.name}</td>
                <td>{life.credit_price}</td>
                <td>
                  <img
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      handleEditLife(life);
                      setIsEdit(true);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleEditLife(life);
                      }
                    }}
                    src={Edit}
                    alt="Editar vida"
                    style={{ marginLeft: '10px', cursor: 'pointer' }}
                    aria-label="Editar vida"
                  />
                  <img
                    role="button"
                    tabIndex={0}
                    onClick={() => handleDeleteLife(life)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDeleteLife(life);
                      }
                    }}
                    src={Delete}
                    alt="Deletar vida"
                    style={{ marginLeft: '10px', cursor: 'pointer' }}
                    aria-label="Deletar vida"
                  />
                </td>
                <td>
                  <img
                    src={ArrowRightRed}
                    alt="Ir"
                    style={goToDetailPageButtonStyle.current}
                  />
                </td>
              </tr>
            ))}
          </Table>
          <S.TableFooter>
            <Button
              onClick={handleOpenSaveOperatorModal}
              containerStyle={newOperatorButtonStyle.current}
            >
              Adicionar vidas
            </Button>
            {/* <Pagination ... /> */}
          </S.TableFooter>
        </S.TableWrapper>
      </S.Container>

      <Suspense fallback={<Loading loading />}>
        <ConfirmDeleteLivesModal
          modalIsVisible={deleteOperatorModal}
          setModalIsVisible={setDeleteOperatorModal}
          leadId={lifeToDelete?.id || ''}
          onSuccess={handleGetLifes}
        />
      </Suspense>

      <Suspense fallback={<Loading loading />}>
        <LifesFormModal
          isEdit={isEdit}
          modalIsVisible={saveOperatorModal}
          setModalIsVisible={setSaveOperatorModal}
          onSubmit={handleSubmitLife}
          initialData={
            editingLife
              ? {
                  id: editingLife.id,
                  quantity: editingLife.name,
                  credits: editingLife.credit_price,
                }
              : undefined
          }
        />
      </Suspense>
    </>
  );
}

export default Lifes;
